import React, { useState } from 'react';
import { ArrowLeft, FileText, RefreshCw, Package, GitCompare } from 'lucide-react';
import PickTicketSummary from './PickTicketSummary';
import SageConverter from './SageConverter';
import ComponentDatabase from './ComponentDatabase';
import PTvsCRR from './PTvsCRR';

const TOOLS = [
  {
    id: 'pick-ticket',
    label: 'Pick Ticket Summary',
    icon: FileText,
    description: 'Parse Sage Picking Sheet PDFs, aggregate items, export to Excel/PDF.',
  },
  {
    id: 'sage-converter',
    label: 'Component Req. Converter',
    icon: RefreshCw,
    description: 'Convert raw Sage Component Reports to clean Excel with formulas.',
  },
  {
    id: 'pt-vs-crr',
    label: 'PT vs CRR',
    icon: GitCompare,
    description: 'Compare Pick Ticket and Component Req. Report to find mismatches.',
  },
];

const ACTIVE_STYLES = {
  'pick-ticket': 'bg-amber-50 border-amber-200',
  'sage-converter': 'bg-emerald-50 border-emerald-200',
  'pt-vs-crr': 'bg-indigo-50 border-indigo-200',
};
const ICON_STYLES = {
  'pick-ticket': 'text-amber-600',
  'sage-converter': 'text-emerald-600',
  'pt-vs-crr': 'text-indigo-600',
};

const SUB_TABS = [
  { id: 'converter', label: 'Converter' },
  { id: 'database', label: 'Component Database' },
];

export default function WarehouseHome({ onBack }) {
  const [activeTool, setActiveTool] = useState('pick-ticket');
  const [sageSubTab, setSageSubTab] = useState('converter');

  const isSageActive = activeTool === 'sage-converter';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-4 max-w-[1600px] mx-auto">
          <button onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
            data-testid="warehouse-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Modules
          </button>
          <div className="h-5 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-600" />
            <h1 className="text-base font-bold text-slate-800">Sage Data Tools</h1>
          </div>
        </div>
      </div>

      <div className="flex flex-1 max-w-[1600px] mx-auto w-full">
        <div className="w-64 bg-white border-r border-slate-200 p-4 flex-shrink-0">
          <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-3 px-2">Tools</div>
          <nav className="space-y-1">
            {TOOLS.map(tool => {
              const Icon = tool.icon;
              const isActive = activeTool === tool.id;
              return (
                <button key={tool.id} onClick={() => setActiveTool(tool.id)}
                  data-testid={`warehouse-nav-${tool.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-3 transition-all border ${
                    isActive ? ACTIVE_STYLES[tool.id] : 'border-transparent hover:bg-slate-50'
                  }`}>
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isActive ? ICON_STYLES[tool.id] : 'text-slate-400'}`} />
                  <div>
                    <div className={`text-sm font-semibold ${isActive ? 'text-slate-800' : 'text-slate-600'}`}>{tool.label}</div>
                    <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{tool.description}</div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          {/* Pick Ticket */}
          <div style={{ display: activeTool === 'pick-ticket' ? 'block' : 'none' }}>
            <PickTicketSummary />
          </div>

          {/* Sage Converter section - always mounted */}
          <div style={{ display: isSageActive ? 'block' : 'none' }}>
            <div className="space-y-4">
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="sage-sub-tabs">
                {SUB_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setSageSubTab(tab.id)}
                    data-testid={`sage-subtab-${tab.id}`}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      sageSubTab === tab.id
                        ? 'bg-white text-slate-800 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <div style={{ display: sageSubTab === 'converter' ? 'block' : 'none' }}>
                <SageConverter />
              </div>
              <div style={{ display: sageSubTab === 'database' ? 'block' : 'none' }}>
                <ComponentDatabase />
              </div>
            </div>
          </div>

          {/* PT vs CRR Comparison */}
          <div style={{ display: activeTool === 'pt-vs-crr' ? 'block' : 'none' }}>
            <PTvsCRR />
          </div>
        </div>
      </div>
    </div>
  );
}
